// Saída estruturada: monta o JSON Schema da resposta por campos (nome, tipo, descrição, obrigatório),
// com objetos e listas aninhados, ou direto no JSON. O que o editor visual não mostra (minimum, format…)
// fica guardado no campo e volta para o schema; anyOf, $ref e afins só no modo JSON.
import React, { useMemo, useRef, useState } from "react";
import { Braces, GripVertical, Hash, List, ListChecks, Plus, ToggleLeft, Type, WandSparkles, X, Binary } from "lucide-react";
import { Button, Seg, cx } from "./ui";
import { CodeEditor } from "./editor";

export type Kind = "string" | "number" | "integer" | "boolean" | "enum" | "array" | "object";
export interface SchemaNode {
  id: string;
  name: string;
  kind: Kind;
  description: string;
  required: boolean;
  nullable: boolean;
  options: string[];
  item?: SchemaNode;
  children: SchemaNode[];
  extra: Record<string, any>;
}

const KINDS: { value: Kind; label: string; icon: React.ReactNode; color: string }[] = [
  { value: "string", label: "Texto", icon: <Type size={12} />, color: "var(--green)" },
  { value: "number", label: "Número", icon: <Hash size={12} />, color: "var(--blue)" },
  { value: "integer", label: "Inteiro", icon: <Binary size={12} />, color: "var(--blue)" },
  { value: "boolean", label: "Sim/não", icon: <ToggleLeft size={12} />, color: "var(--amber)" },
  { value: "enum", label: "Opções", icon: <ListChecks size={12} />, color: "var(--violet)" },
  { value: "array", label: "Lista", icon: <List size={12} />, color: "var(--text-2)" },
  { value: "object", label: "Objeto", icon: <Braces size={12} />, color: "var(--text-2)" },
];
const kindOf = (k: Kind) => KINDS.find((x) => x.value === k)!;

let seq = 0;
const nid = () => "f" + ++seq + Math.random().toString(36).slice(2, 6);
export const blankNode = (name = "", kind: Kind = "string"): SchemaNode => ({ id: nid(), name, kind, description: "", required: true, nullable: false, options: [], children: [], extra: {} });

// ---------- schema → campos ----------

const HANDLED = new Set(["type", "description", "enum", "items", "properties", "required", "additionalProperties"]);

function nodeFrom(name: string, s: any, required: boolean): SchemaNode | null {
  if (!s || typeof s !== "object" || Array.isArray(s)) return null;
  if (s.anyOf || s.oneOf || s.allOf || s.$ref || s.not || s.const !== undefined || s.$defs || s.definitions) return null;
  let types: string[] = Array.isArray(s.type) ? [...s.type] : s.type ? [s.type] : [];
  let nullable = types.includes("null");
  types = types.filter((t) => t !== "null");
  if (types.length > 1) return null;
  let t = types[0];
  if (!t) t = s.enum ? "string" : s.properties ? "object" : s.items ? "array" : "";
  if (!t) return null;
  const extra = Object.fromEntries(Object.entries(s).filter(([k]) => !HANDLED.has(k)));
  const n: SchemaNode = { ...blankNode(name), required, nullable, description: typeof s.description === "string" ? s.description : "", extra };
  if (Array.isArray(s.enum)) {
    if (t !== "string" || !s.enum.every((v: any) => typeof v === "string" || v === null)) return null;
    n.kind = "enum";
    n.options = s.enum.filter((v: any) => v !== null);
    if (s.enum.includes(null)) n.nullable = true;
    return n;
  }
  if (t === "object") {
    const kids = childrenFrom(s);
    if (!kids) return null;
    n.kind = "object";
    n.children = kids;
    if (s.additionalProperties !== undefined && s.additionalProperties !== false) n.extra.additionalProperties = s.additionalProperties;
    return n;
  }
  if (t === "array") {
    const item = s.items === undefined ? blankNode() : nodeFrom("", s.items, true);
    if (!item) return null;
    n.kind = "array";
    n.item = item;
    return n;
  }
  if (t === "string" || t === "number" || t === "integer" || t === "boolean") {
    n.kind = t;
    return n;
  }
  return null;
}

function childrenFrom(s: any): SchemaNode[] | null {
  if (s.properties !== undefined && (typeof s.properties !== "object" || Array.isArray(s.properties))) return null;
  const req = new Set(Array.isArray(s.required) ? s.required : []);
  const out: SchemaNode[] = [];
  for (const [k, v] of Object.entries(s.properties ?? {})) {
    const n = nodeFrom(k, v, req.has(k));
    if (!n) return null;
    out.push(n);
  }
  return out;
}

export function fromSchema(schema: any): { fields: SchemaNode[]; root: Record<string, any> } | null {
  if (schema === undefined || schema === null) return { fields: [], root: {} };
  if (typeof schema !== "object" || Array.isArray(schema)) return null;
  if (!Object.keys(schema).length) return { fields: [], root: {} };
  if (schema.type !== "object" && !(schema.type === undefined && schema.properties)) return null;
  const root = nodeFrom("", schema, true);
  if (!root || root.kind !== "object" || root.nullable) return null;
  return { fields: root.children, root: root.extra };
}

// ---------- campos → schema ----------

function nodeTo(n: SchemaNode): any {
  const ty = (t: string) => (n.nullable ? [t, "null"] : t);
  const out: any = {};
  if (n.kind === "enum") {
    out.type = ty("string");
    out.enum = n.nullable ? [...n.options, null] : [...n.options];
  } else if (n.kind === "object") {
    Object.assign(out, objectTo(n.children));
    if (n.nullable) out.type = ["object", "null"];
  } else if (n.kind === "array") {
    out.type = ty("array");
    out.items = nodeTo(n.item ?? blankNode());
  } else out.type = ty(n.kind);
  const d = n.description.trim();
  const ordered: any = { type: out.type };
  if (d) ordered.description = d;
  return { ...ordered, ...out, ...n.extra };
}

function objectTo(children: SchemaNode[]) {
  const properties: Record<string, any> = {};
  const required: string[] = [];
  for (const c of children) {
    const k = c.name.trim();
    if (!k || k in properties) continue;
    properties[k] = nodeTo(c);
    if (c.required) required.push(k);
  }
  return { type: "object", properties, ...(required.length ? { required } : {}), additionalProperties: false };
}

export function toSchema(fields: SchemaNode[], root: Record<string, any> = {}): any {
  if (!fields.length) return undefined;
  return { ...objectTo(fields), ...root };
}

// ---------- exemplo → campos ----------

function infer(name: string, v: any): SchemaNode {
  const n = blankNode(name);
  if (v === null || v === undefined) return { ...n, nullable: true };
  if (typeof v === "boolean") return { ...n, kind: "boolean" };
  if (typeof v === "number") return { ...n, kind: Number.isInteger(v) ? "integer" : "number" };
  if (typeof v === "string") return n;
  if (Array.isArray(v)) {
    // junta os itens: um objeto com todas as chaves vistas em qualquer item
    const objs = v.filter((x) => x && typeof x === "object" && !Array.isArray(x));
    const item = objs.length ? infer("", Object.assign({}, ...objs)) : infer("", v.find((x) => x !== null && x !== undefined) ?? "");
    return { ...n, kind: "array", item };
  }
  return { ...n, kind: "object", children: Object.entries(v).map(([k, x]) => infer(k, x)) };
}

export function fieldsFromExample(text: string): SchemaNode[] {
  const v = JSON.parse(text);
  if (!v || typeof v !== "object" || Array.isArray(v)) throw new Error("O exemplo precisa ser um objeto JSON, como { \"campo\": \"valor\" }.");
  return Object.entries(v).map(([k, x]) => infer(k, x));
}

// ---------- componente ----------

export function SchemaEditor({ schema, onChange, readOnly }: { schema: any; onChange: (schema: any) => void; readOnly?: boolean }) {
  const initial = useMemo(() => fromSchema(schema), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [mode, setMode] = useState<"fields" | "json">(initial ? "fields" : "json");
  const [fields, setFields] = useState<SchemaNode[]>(initial?.fields ?? []);
  const root = useRef<Record<string, any>>(initial?.root ?? {});
  const [text, setText] = useState(() => JSON.stringify(schema ?? {}, null, 2));
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [example, setExample] = useState<string | null>(null);
  const [exampleErr, setExampleErr] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);

  const emit = (next: SchemaNode[]) => {
    setFields(next);
    const s = toSchema(next, root.current);
    setText(JSON.stringify(s ?? {}, null, 2));
    onChange(s);
  };

  const switchMode = (m: "fields" | "json") => {
    setNotice(null);
    if (m === "fields") {
      let parsed: any;
      try {
        parsed = text.trim() ? JSON.parse(text) : undefined;
      } catch {
        setNotice("Corrija o JSON antes de voltar para os campos.");
        return;
      }
      const f = fromSchema(parsed);
      if (!f) {
        setNotice("Este schema usa recursos que os campos não mostram (anyOf, $ref, mais de um tipo…). Continue no JSON.");
        return;
      }
      root.current = f.root;
      setFields(f.fields);
    }
    setMode(m);
  };

  const add = () => {
    const n = blankNode();
    setFocusId(n.id);
    emit([...fields, n]);
  };

  const generate = () => {
    try {
      const f = fieldsFromExample(example ?? "");
      if (!f.length) throw new Error("O exemplo não tem nenhum campo.");
      root.current = {};
      setMode("fields");
      emit(f);
      setExample(null);
      setExampleErr(null);
    } catch (e: any) {
      setExampleErr(e.message);
    }
  };

  const count = useMemo(() => countFields(fields), [fields]);

  return (
    <div className="sf">
      <div className="sf-bar">
        <Seg value={mode} onChange={switchMode} options={[{ value: "fields", label: "Campos", icon: <ListChecks size={12} /> }, { value: "json", label: "JSON Schema", icon: <Braces size={12} /> }]} />
        <span className="row" style={{ gap: 10 }}>
          {mode === "fields" && count.total > 0 && <span className="faint" style={{ fontSize: 12 }}>{count.total} {count.total === 1 ? "campo" : "campos"} · {count.required} obrigatório{count.required === 1 ? "" : "s"}</span>}
          {!readOnly && example === null && <Button size="sm" variant="ghost" icon={<WandSparkles size={13} />} onClick={() => setExample("")}>Gerar de exemplo</Button>}
        </span>
      </div>
      {notice && <div className="field-hint" style={{ color: "var(--amber)" }}>{notice}</div>}

      {example !== null && (
        <div className="sf-example">
          <div className="field-hint">Cole uma resposta como você quer receber. Os campos, tipos e listas saem dela; depois é só ajustar.</div>
          <CodeEditor language="json" value={example} onChange={setExample} minHeight={90} maxHeight={260} placeholder={'{ "numero": "123", "valor": 10.5, "itens": [{ "nome": "café" }] }'} onSubmit={generate} />
          {exampleErr && <div className="field-hint" style={{ color: "var(--red)" }}>{exampleErr}</div>}
          <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
            <Button size="sm" variant="ghost" onClick={() => (setExample(null), setExampleErr(null))}>Cancelar</Button>
            <Button size="sm" variant="primary" icon={<WandSparkles size={13} />} onClick={generate} disabled={!example?.trim()}>{fields.length ? "Substituir campos" : "Gerar campos"}</Button>
          </div>
        </div>
      )}

      {mode === "json" ? (
        <>
          <CodeEditor
            language="json"
            numbers
            value={text}
            readOnly={readOnly}
            minHeight={120}
            onChange={(v) => {
              setText(v);
              try {
                const parsed = v.trim() ? JSON.parse(v) : undefined;
                setJsonErr(null);
                onChange(parsed && Object.keys(parsed).length ? parsed : undefined);
              } catch (e: any) {
                setJsonErr(e.message);
              }
            }}
          />
          <div className="field-hint" style={{ color: jsonErr ? "var(--red)" : undefined }}>{jsonErr ?? "JSON Schema da resposta. Vazio = qualquer objeto JSON."}</div>
        </>
      ) : fields.length === 0 ? (
        <div className="sf-empty">
          <span>Sem campos, o modelo devolve qualquer objeto JSON.</span>
          {!readOnly && (
            <span className="row" style={{ gap: 6 }}>
              <Button size="sm" icon={<Plus size={13} />} onClick={add}>Adicionar campo</Button>
              {example === null && <Button size="sm" variant="ghost" icon={<WandSparkles size={13} />} onClick={() => setExample("")}>Gerar de um exemplo</Button>}
            </span>
          )}
        </div>
      ) : (
        <>
          <FieldList nodes={fields} onChange={emit} readOnly={readOnly} focusId={focusId} setFocusId={setFocusId} />
          {!readOnly && <Button size="sm" variant="ghost" className="sf-add" icon={<Plus size={13} />} onClick={add}>Campo</Button>}
        </>
      )}
    </div>
  );
}

function countFields(nodes: SchemaNode[]): { total: number; required: number } {
  let total = 0;
  let required = 0;
  for (const n of nodes) {
    total++;
    if (n.required) required++;
  }
  return { total, required };
}

type ListProps = { nodes: SchemaNode[]; onChange: (n: SchemaNode[]) => void; readOnly?: boolean; focusId: string | null; setFocusId: (id: string | null) => void };

function FieldList({ nodes, onChange, readOnly, focusId, setFocusId }: ListProps) {
  const [drag, setDrag] = useState<string | null>(null);
  const names = nodes.map((n) => n.name.trim());
  const moveTo = (overId: string) => {
    if (!drag || drag === overId) return;
    const from = nodes.findIndex((n) => n.id === drag);
    const to = nodes.findIndex((n) => n.id === overId);
    if (from < 0 || to < 0) return;
    const next = [...nodes];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };
  return (
    <div className="sf-list">
      {nodes.map((n, i) => {
        const nm = n.name.trim();
        const bad = !nm ? "Dê um nome ao campo" : names.indexOf(nm) !== i ? "Nome repetido" : null;
        return (
          <div
            key={n.id}
            className={cx("sf-row", drag === n.id && "dragging")}
            onDragOver={(e) => {
              if (!drag) return;
              e.preventDefault();
              e.stopPropagation();
              moveTo(n.id);
            }}
          >
            <FieldRow
              node={n}
              bad={bad}
              readOnly={readOnly}
              autoFocus={focusId === n.id}
              onFocused={() => setFocusId(null)}
              onChange={(m) => onChange(nodes.map((x) => (x.id === n.id ? m : x)))}
              onRemove={() => onChange(nodes.filter((x) => x.id !== n.id))}
              grip={
                !readOnly && (
                  <span
                    className="sf-grip"
                    draggable
                    title="Arraste para reordenar"
                    onDragStart={(e) => {
                      e.stopPropagation();
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", n.id);
                      const row = (e.currentTarget as HTMLElement).closest(".sf-row");
                      if (row) e.dataTransfer.setDragImage(row, 12, 14);
                      setDrag(n.id);
                    }}
                    onDragEnd={() => setDrag(null)}
                  >
                    <GripVertical size={13} />
                  </span>
                )
              }
              focusId={focusId}
              setFocusId={setFocusId}
            />
          </div>
        );
      })}
    </div>
  );
}

function KindSelect({ value, onChange, disabled }: { value: Kind; onChange: (k: Kind) => void; disabled?: boolean }) {
  const k = kindOf(value);
  return (
    <span className="sf-type" style={{ ["--k" as any]: k.color }}>
      {k.icon}
      <select className="select select-sm" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as Kind)}>
        {KINDS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
      </select>
    </span>
  );
}

// Trocar o tipo preserva o que faz sentido (nome, descrição, obrigatório) e começa os filhos quando precisa.
function withKind(n: SchemaNode, kind: Kind): SchemaNode {
  const m: SchemaNode = { ...n, kind, extra: kind === n.kind ? n.extra : {} };
  if (kind === "array" && !m.item) m.item = blankNode();
  if (kind === "object" && !m.children.length) m.children = [blankNode()];
  return m;
}

function FieldRow({ node, bad, readOnly, autoFocus, onFocused, onChange, onRemove, grip, focusId, setFocusId }: {
  node: SchemaNode; bad: string | null; readOnly?: boolean; autoFocus: boolean; onFocused: () => void; onChange: (n: SchemaNode) => void; onRemove: () => void; grip: React.ReactNode; focusId: string | null; setFocusId: (id: string | null) => void;
}) {
  const set = (patch: Partial<SchemaNode>) => onChange({ ...node, ...patch });
  return (
    <>
      <div className="sf-main">
        {grip || <span className="sf-grip-space" />}
        <input
          className={cx("sf-name", bad && node.name !== "" && "bad")}
          value={node.name}
          placeholder="nome_do_campo"
          title={bad ?? undefined}
          disabled={readOnly}
          spellCheck={false}
          autoFocus={autoFocus}
          onFocus={onFocused}
          onChange={(e) => set({ name: e.target.value.replace(/\s/g, "_") })}
        />
        <KindSelect value={node.kind} disabled={readOnly} onChange={(k) => onChange(withKind(node, k))} />
        <button type="button" className={cx("sf-pill req", node.required && "on")} disabled={readOnly} onClick={() => set({ required: !node.required })} title="Obrigatório: o modelo sempre preenche este campo">
          {node.required ? "obrigatório" : "opcional"}
        </button>
        <button type="button" className={cx("sf-pill", node.nullable && "on")} disabled={readOnly} onClick={() => set({ nullable: !node.nullable })} title="Aceita null quando a informação não existe">
          null
        </button>
        {!readOnly && (
          <button type="button" className="sf-x" onClick={onRemove} title="Remover campo">
            <X size={13} />
          </button>
        )}
      </div>
      <input className="sf-desc" value={node.description} disabled={readOnly} placeholder="Descrição: o que o modelo deve pôr aqui" onChange={(e) => set({ description: e.target.value })} />
      <KindBody node={node} onChange={onChange} readOnly={readOnly} focusId={focusId} setFocusId={setFocusId} />
    </>
  );
}

// O que vem embaixo do campo, conforme o tipo: opções, item da lista ou campos do objeto.
function KindBody({ node, onChange, readOnly, focusId, setFocusId }: { node: SchemaNode; onChange: (n: SchemaNode) => void; readOnly?: boolean; focusId: string | null; setFocusId: (id: string | null) => void }) {
  if (node.kind === "enum") return <OptionsInput options={node.options} readOnly={readOnly} onChange={(options) => onChange({ ...node, options })} />;
  if (node.kind === "object") {
    return (
      <div className="sf-children">
        <FieldList nodes={node.children} onChange={(children) => onChange({ ...node, children })} readOnly={readOnly} focusId={focusId} setFocusId={setFocusId} />
        {!readOnly && (
          <Button size="xs" variant="ghost" className="sf-add" icon={<Plus size={12} />} onClick={() => { const n = blankNode(); setFocusId(n.id); onChange({ ...node, children: [...node.children, n] }); }}>
            Campo em {node.name || "objeto"}
          </Button>
        )}
      </div>
    );
  }
  if (node.kind === "array") {
    const item = node.item ?? blankNode();
    const setItem = (m: SchemaNode) => onChange({ ...node, item: m });
    return (
      <div className="sf-children">
        <div className="sf-item">
          <span className="faint">cada item é</span>
          <KindSelect value={item.kind} disabled={readOnly} onChange={(k) => setItem(withKind(item, k))} />
          {item.kind !== "object" && (
            <input className="sf-desc" style={{ margin: 0, flex: 1 }} value={item.description} disabled={readOnly} placeholder="descrição do item (opcional)" onChange={(e) => setItem({ ...item, description: e.target.value })} />
          )}
        </div>
        <KindBody node={item} onChange={setItem} readOnly={readOnly} focusId={focusId} setFocusId={setFocusId} />
      </div>
    );
  }
  return null;
}

function OptionsInput({ options, onChange, readOnly }: { options: string[]; onChange: (o: string[]) => void; readOnly?: boolean }) {
  const [draft, setDraft] = useState("");
  const commit = (raw: string) => {
    const vals = raw.split(",").map((s) => s.trim()).filter((s) => s && !options.includes(s));
    if (vals.length) onChange([...options, ...vals]);
    setDraft("");
  };
  return (
    <div className="sf-opts" onClick={(e) => (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.focus()}>
      {options.map((o) => (
        <span key={o} className="sf-opt">
          {o}
          {!readOnly && <X size={11} onClick={(e) => (e.stopPropagation(), onChange(options.filter((x) => x !== o)))} />}
        </span>
      ))}
      {!readOnly && (
        <input
          value={draft}
          placeholder={options.length ? "mais uma…" : "opções: digite e Enter (ou separe por vírgula)"}
          onChange={(e) => (e.target.value.includes(",") ? commit(e.target.value) : setDraft(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.preventDefault(), commit(draft));
            else if (e.key === "Backspace" && !draft && options.length) onChange(options.slice(0, -1));
          }}
          onBlur={() => draft.trim() && commit(draft)}
        />
      )}
      {!options.length && readOnly && <span className="faint">sem opções</span>}
    </div>
  );
}

